"use client";

import { useState } from "react";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { usePathname, useRouter } from "next/navigation";
import CustomSelect from "../../../../components/theme/CustomSelect";
import Cookies from "js-cookie";
import { useQueryClient } from "@tanstack/react-query";
import { useSeoURLStore } from "@/components/context/seo-lang-store";

type LOCALES = "de" | "en" | "fr" | "it" | "pseudo";

const languages = {
  en: msg`English`,
  de: msg`Deutsch`,
  fr: msg`French`,
  it: msg`Italian`,
} as const;

interface LanguageSwitcherProps {
  darkMode: boolean;
}

export function LanguageSwitcher({ darkMode }: LanguageSwitcherProps) {
  const router = useRouter();
  const { i18n } = useLingui();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { seoLinks } = useSeoURLStore();

  const [locale, setLocale] = useState<LOCALES>(pathname?.split("/")[1] as LOCALES);

  // disabled for DEMO - so we can demonstrate the 'pseudo' locale functionality
  // if (process.env.NEXT_PUBLIC_NODE_ENV !== 'production') {
  //   languages['pseudo'] = t`Pseudo`
  // }

  const handleChange = (id: string) => {
    const locale = id as LOCALES;
    const pathNameWithoutLocale = pathname?.split("/")?.slice(2) ?? [];
    const hasSeoLinks = ["category", "product", "information"];
    if (seoLinks && hasSeoLinks.includes(pathNameWithoutLocale[0])) {
      pathNameWithoutLocale[pathNameWithoutLocale.length - 1] = seoLinks[locale];
    }
    const newPath = `/${locale}/${pathNameWithoutLocale.join("/")}`;
    queryClient.removeQueries();
    Cookies.set("locale", locale);
    setLocale(locale);
    router.push(newPath);
  };

  return (
    <CustomSelect
      darkMode={darkMode}
      onChange={handleChange}
      selected={{
        text: i18n._(languages[locale as keyof typeof languages]),
      }}
      options={Object.keys(languages).map((item) => {
        return {
          id: item,
          text: i18n._(languages[item as keyof typeof languages]),
        };
      })}
      type="language"
    />
  );
}
